import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Box, ButtonBase, makeStyles } from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';
import { DragDropContext } from 'react-beautiful-dnd';
import _, { isFunction } from 'lodash';
import { useSelector } from 'react-redux';
import Toolbar from '../Toolbar';
import { ArrowLeftIcon } from '../../../../components/Icon';
import { i18n } from '../../../../language';
import { bedStatusAPI } from '../../../../apis';
import { BS_TOOLBAR_STATUS } from '../../../../comm/constant';
import {
  createLinkBed,
  getBedByCubicleIdAndPosition,
  getMainLinkedBed,
  handleDragFromCubicle,
  handleDragFromWaitingList,
  updateBedByCubicleIdAndPosition,
} from '../../../../utils/newBedStatusUtil';
import FirstLinkedBed from '../Popups/FirstLinkedBed';
import StyleButton from '../../../../components/StyleButton';
import Dialog from '../../../../components/Dialog';
import rou from '../../../../router';
import useIpad from '../../../../utils/useIpad';
import { useActionableTicketReminder } from '../../../../utils/hooks';
import FloorPlan from './FloorPlan';
import BedsWaitingPlace from './BedsWaitingPlace';

const useStyles = makeStyles(() => ({
  header: {
    backgroundColor: '#ffffff',
    height: '4.5rem',
    boxShadow: '0 1px 0 0 #e4e7eb',
    padding: '0 3.438rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #e4e7eb',
  },
  arrowLeftIcon: {
    fontSize: '1rem',
    color: '#538fff',
  },
  back: {
    paddingLeft: '1rem',
    fontSize: '1rem',
    color: '#538fff',
    fontFamily: 'NotoSans-Bold',
  },
}));

const EditFloorPlan = () => {
  const classes = useStyles();
  const [toolStatus, setToolStatus] = useState({ type: BS_TOOLBAR_STATUS.MOVE, zoom: 1, reset: 0 });
  const history = useHistory();
  const { id } = useParams();
  const loginInfo = useSelector((state) => state.loginInfo);
  const loginInfoRef = useRef(false);
  const [createPopOpen, setCreatePopOpen] = useState(false);
  const [floorPlanData, setFloorPlanData] = useState();
  const [bedList, setBedList] = useState([]);
  const [update, setUpdate] = useState(false);
  const [dragedBedList, setDragedBedList] = useState([]);
  const [reload, setReload] = useState(0);
  const [cubicleList, setCubicleList] = useState([]);
  const [firstLinkPopup, setFirstLinkPopup] = useState(false);
  const [firstLinkObj, setFirstLinkObj] = useState({
    from: {},
    to: {},
    type: '',
  });
  const [rotate, setRotate] = useState('top');
  const isPad = useIpad();
  const [ipadWarnPopup, setIpadWarnPopup] = useState(false);
  const [leave, setLeave] = useState(false);
  useActionableTicketReminder('audio', 'BS');

  useEffect(() => {
    setIpadWarnPopup(isPad);
  }, [isPad]);

  const handleBack = useCallback(() => {
    history.goBack();
  }, []);

  const handleCreatePopup = useCallback(
    (open) => () => {
      setCreatePopOpen(open);
    },
    []
  );

  const getCubicleListAPI = useCallback(
    async (callback) => {
      try {
        const { result } = await bedStatusAPI.getCubicleList({ floorPlanId: id });
        setFloorPlanData(result);
        if (isFunction(callback)) {
          callback();
        }
      } catch (error) {
        console.log(error);
      }
    },
    [id]
  );

  useEffect(() => {
    getCubicleListAPI();
  }, [getCubicleListAPI, reload]);

  const getLinkListAPI = useCallback(async () => {
    try {
      const { result } = await bedStatusAPI.getLinkedList({
        hospCode: floorPlanData?.hospCode,
        wardLocation: floorPlanData?.wardLocation,
      });
      setBedList(result);
    } catch (error) {}
  }, [floorPlanData]);

  useEffect(() => {
    if (floorPlanData) {
      getLinkListAPI();
    }
  }, [getLinkListAPI, floorPlanData]);

  const waitBeds = useMemo(() => {
    let tempList = [];

    tempList = _.flatMap(bedList, (item) => item.wardBeds);

    return _.map(tempList, (item) => ({
      ...item,
      bedHead: rotate,
    }));
  }, [bedList, rotate]);

  const handleDragEnd = useCallback(
    (e) => {
      if (e.destination && e.destination !== e.source) {
        const source = JSON.parse(e.source.droppableId);
        const destination = JSON.parse(e.destination.droppableId);
        if (
          source.cubicleId === destination.cubicleId &&
          source.row === destination.row &&
          source.column === destination.column
        ) {
          // 防止拖拽在原来的位置放下
          return;
        }
        const destinationBed = getBedByCubicleIdAndPosition(
          cubicleList,
          destination.cubicleId,
          destination.row,
          destination.column
        );

        switch (source.type) {
          case 'Wait':
            handleDragFromWaitingList({
              waitBeds,
              e,
              destinationBed,
              setCubicleList,
              destination,
              setDragedBedList,
              setFirstLinkPopup,
              setFirstLinkObj,
            });
            break;
          case 'Cubicle':
            handleDragFromCubicle({
              destinationBed,
              cubicleList,
              source,
              setCubicleList,
              destination,
              setFirstLinkPopup,
              setFirstLinkObj,
            });
            break;

          default:
            break;
        }
      }
    },
    [waitBeds, cubicleList]
  );

  const handleCloseFirstLinkPop = useCallback(() => {
    setFirstLinkPopup(false);
  }, []);

  const handleFirstCreateLink = useCallback(
    (form, sourceBed, destinationBed) => {
      setFirstLinkPopup(false);
      setFirstLinkObj({
        from: {},
        to: {},
        type: '',
      });
      const linkedBed = createLinkBed(sourceBed, destinationBed, form.linkedBedNo);

      // 清掉拖放的床位
      if (firstLinkObj.type === 'Wait') {
        setDragedBedList((o) => [...o, firstLinkObj.from.bed]);
      } else {
        updateBedByCubicleIdAndPosition({
          setCubicleList,
          cubicleId: firstLinkObj.from.cubicleId,
          row: firstLinkObj.from.row,
          column: firstLinkObj.from.column,
          data: null,
        });
      }
      // 更新link之后的床位
      updateBedByCubicleIdAndPosition({
        setCubicleList,
        cubicleId: firstLinkObj.to.cubicleId,
        row: firstLinkObj.to.row,
        column: firstLinkObj.to.column,
        data: linkedBed,
      });
    },
    [firstLinkObj]
  );

  const handleRemoveLinkedBed = useCallback((cubicleId, bedData, handleClose) => {
    updateBedByCubicleIdAndPosition({
      setCubicleList,
      cubicleId: cubicleId,
      row: bedData.bedRow,
      column: bedData.bedColumn,
      data: null,
    });
    setDragedBedList((o) =>
      _.filter(o, (item) => !_.some(bedData.linkeds, (linkedItem) => linkedItem.bedStatusId === item.bedStatusId))
    );
    handleClose();
  }, []);

  // 修改link
  const handleUpdateLink = useCallback(
    (cubicleId, bedData, form, handleClose) => {
      const newMainBed = getMainLinkedBed(form.linkeds);
      updateBedByCubicleIdAndPosition({
        setCubicleList,
        cubicleId: cubicleId,
        row: bedData.bedRow,
        column: bedData.bedColumn,
        data: { ...newMainBed, linkedBedNo: form.linkedBedNo, linkeds: form.linkeds },
      });
      setDragedBedList((o) => {
        return _.filter(
          o,
          (item) => !_.some(form.deletedBeds, (deletedItem) => deletedItem.bedStatusId === item.bedStatusId)
        );
      });
      handleClose();
    },
    [floorPlanData]
  );

  // waiting list 里面创建 link
  const handleWaitCreateLinkedBed = useCallback(
    (data, handleClose) => {
      _.forEach(data, (item) => {
        // 提取 cubicle 上面的床位出来
        const cubicleBeds = _.flatMap(cubicleList, (item) => item.data.beds).filter((item) => item.data);
        // 提取 waiting list 里面匹配上的床位
        const sourceBed = _.find(waitBeds, (bedItem) => bedItem.bedStatusId === item.bedStatusId);
        // 提取cubicle上面所有bedNo相同的床位
        const destination = _.filter(cubicleBeds, (bedItem) =>
          _.some(item.links, (linksItem) => linksItem.bedStatusId === bedItem.data.bedStatusId)
        );
        // 根据cubicle上面的床位，获取优先级最高的床位，linked之后放在这里
        const destinationBed = getMainLinkedBed(_.flatMap(destination, (destinationItem) => destinationItem.data));
        // 根据所有linked bed，获取优先级最高的床位作为主床位
        const linkedBed = getMainLinkedBed([
          ..._.flatMap(destination, (destinationItem) => destinationItem.data),
          sourceBed,
        ]);

        // 更新 floor plan 以及 waiting list 上面的数据
        _.forEach(destination, (destinationItem) => {
          // 获取cubicle匹配到床位的cubicle id
          const cubicleId = _.find(cubicleList, (cubicleItem) =>
            _.some(cubicleItem.data.beds, (cubicleBedItem) => {
              return cubicleBedItem.data && cubicleBedItem.data.bedStatusId === destinationItem.data.bedStatusId;
            })
          ).id;
          if (destinationItem.data.bedStatusId === destinationBed.bedStatusId) {
            // 在主床位上更新
            updateBedByCubicleIdAndPosition({
              setCubicleList,
              cubicleId: cubicleId,
              row: destinationItem.row,
              column: destinationItem.column,
              data: {
                ...linkedBed,
                bedHead: destinationBed.bedHead,
                linkedBedNo: item.linkedBedName,
                linkeds: _.map(
                  [..._.flatMap(destination, (destinationItem) => destinationItem.data.linkeds), sourceBed],
                  (linkedItem) => ({
                    bedNo: linkedItem.bedNo,
                    bedStatusId: linkedItem.bedStatusId,
                    ccWardCd: linkedItem.ccWardCd,
                    status: linkedItem.status,
                  })
                ),
              },
            });
          } else {
            // 不是主床位就清除数据
            updateBedByCubicleIdAndPosition({
              setCubicleList,
              cubicleId: cubicleId,
              row: destinationItem.row,
              column: destinationItem.column,
              data: null,
            });
          }
        });
        setDragedBedList((o) => [...o, sourceBed]);
      });
      handleClose();
    },
    [cubicleList, waitBeds]
  );

  // 在header里的所有事件
  const headerEvent = useMemo(() => {
    const handleClearAll = () => {
      setCubicleList([]);
      setDragedBedList([]);
    };

    const handleUpdate = () => {
      setUpdate(true);
    };

    const handleReload = () => {
      setReload((o) => o + 1);
    };

    return {
      handleClearAll,
      handleUpdate,
      handleReload,
    };
  }, []);

  // 在ipad里弹出warning之后的点击事件
  const handleIpadWarning = useCallback(() => {
    history.push(rou.bedstatusNewRou.path);
  }, [history]);

  useEffect(() => {
    // 如果在这个页面切换了ward，跳回去bed status page
    // 第一次进来的时候不操作
    if (!loginInfoRef.current) {
      loginInfoRef.current = true;
      return;
    }
    history.push(rou.bedstatusNewRou.path);
  }, [loginInfo]);

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box position="relative">
        <div className={classes.header}>
          <ButtonBase onClick={() => setLeave(true)}>
            <ArrowLeftIcon className={classes.arrowLeftIcon} />
            <div className={classes.back}>{i18n.t('BEDSTATUS_BACK')}</div>
          </ButtonBase>
          <Box display="flex" gridGap="1rem">
            <StyleButton onClick={headerEvent.handleClearAll} width="11.25rem" height="2.75rem" text="Clear All" />
            <StyleButton
              onClick={headerEvent.handleReload}
              width="11.25rem"
              height="2.75rem"
              text="Reset"
              type="fill"
              backgroundColor="#538fff"
            />
            <StyleButton
              onClick={headerEvent.handleUpdate}
              width="11.25rem"
              height="2.75rem"
              text="Update"
              type="fill"
              backgroundColor="#28d2ab"
            />
          </Box>
        </div>
        <Box display="flex" padding="1rem 1rem 0 0rem" gridGap="0.8rem">
          <Toolbar type="edit" setStatus={setToolStatus} status={toolStatus} handleCreatePopup={handleCreatePopup} />
          <FloorPlan
            tool={toolStatus}
            setToolStatus={setToolStatus}
            createPopOpen={createPopOpen}
            handleCreatePopup={handleCreatePopup}
            floorPlanData={floorPlanData}
            update={update}
            setUpdate={setUpdate}
            dragedBedList={dragedBedList}
            setDragedBedList={setDragedBedList}
            cubicleList={cubicleList}
            setCubicleList={setCubicleList}
            handleRemoveLinkedBed={handleRemoveLinkedBed}
            handleUpdateLink={handleUpdateLink}
            getCubicleListAPI={getCubicleListAPI}
          />
        </Box>
        <BedsWaitingPlace
          bedList={bedList}
          hospCode={floorPlanData?.hospCode}
          wardLocation={floorPlanData?.wardLocation}
          getLinkListAPI={getLinkListAPI}
          dragedBedList={dragedBedList}
          rotate={rotate}
          setRotate={setRotate}
          cubicleList={cubicleList}
          handleWaitCreateLinkedBed={handleWaitCreateLinkedBed}
        />
      </Box>
      {firstLinkPopup && (
        <FirstLinkedBed
          handleClose={handleCloseFirstLinkPop}
          cubicleList={cubicleList}
          firstLinkObj={firstLinkObj}
          handleFirstCreateLink={handleFirstCreateLink}
        />
      )}
      {ipadWarnPopup && (
        <Dialog
          open={true}
          title="Reminder"
          confirm="Back"
          handleConfirm={handleIpadWarning}
          handleClose={handleIpadWarning}
        >
          Edit floor plan by iPad is not supported. Kindly edit by workstation.
        </Dialog>
      )}
      {leave && (
        <Dialog
          open={true}
          title="Leave page"
          cancel="Cancel"
          confirm="Confirm"
          handleCancel={() => setLeave(false)}
          handleClose={() => setLeave(false)}
          handleConfirm={handleBack}
        >
          Unsaved template will get lost. Please confirm whether to leave this page
        </Dialog>
      )}
    </DragDropContext>
  );
};

export default EditFloorPlan;
